import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Form, Icon, Popover, Tooltip } from '@syneto/compass-react';
import moment from 'moment';
import { trimString } from '../../../helpers/string';
import DateFormatter from '../../../helpers/DateFormatter';
import MaintenanceStatusFormatter from '../../../helpers/MaintenanceStatusFormatter';
import { ADDONS_PANEL_LOADING, ADDONS_PANEL_STATUS } from '../buildAddonsPanel';
import { VALIDITY_STATUS, getMaintenanceExpiry } from '../../../helpers/ExpiryRule';
import { AddonsSection } from './AddonsSection';
import { ValidityBadge } from './ValidityBadge';
import CurrentlyActiveMaintenancePlan from '../../../helpers/CurrentlyActiveMaintenancePlan';
import { parseOSVersion, isOSVersionAtLeast } from '../../../helpers/OSVersionParser';
import { NotificationContext } from '../../../contexts/NotificationContext';
import { Loading } from '../../../components/Loading';
import { StoreContext } from '../../../contexts/StoreContext';
import { ServiceContext } from '../../../contexts/ServiceContext';
import { SerenityEventsButton } from './SerenityEventsButton';
import { UpgradeChannelBadge } from './UpgradeChannelBadge';
import InfoIcon from '../../../components/Form/InfoIcon';

const MINIMUM_PREVIEW_UPDATES_OS_VERSION = '6.0.5';
const OPEN_A_TICKET_URL = 'https://helpdesk.syneto.eu/support/tickets/new';

export const MachineDetails = (props) => {
	const {
		machine,
		generateMaintenanceCertificationPdf,
		partnerPreviewParticipating,
		partnerPreviewEnrollmentEnabled,
		partnerPreviewEnrollmentsRefreshCount,
		currentUpgradeChannel,
		canManagePartnerPreview,
		onTogglePreviewUpdates,
		addons = ADDONS_PANEL_LOADING,
		expiringInterval = null,
		addonsSectionRequest = null,
		onAddonsSectionRequestHandled,
	} = props;
	const { settingsService } = useContext(ServiceContext);
	const { pushSuccessfulNotification, pushDangerNotification } = useContext(NotificationContext);
	const { state } = useContext(StoreContext);
	const { loggedInUser } = state;
	// machine.user is null when the company users are not available to the caller, or when the
	// activating user is no longer in the company.
	const activatedByName = machine.user
		? `${machine.user.profile.firstName} ${machine.user.profile.lastName}`
		: null;

	const [serenityEventsSettings, setSerenityEventsSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [displayedPreviewUpdatesEnabled, setDisplayedPreviewUpdatesEnabled] = useState(
		Boolean(partnerPreviewEnrollmentEnabled)
	);
	// Scoped to the machine whose toggle is in flight, not a plain boolean: `<MachineDetails>`
	// is reconciled rather than remounted on a machine switch (no `key`), so a single shared
	// boolean would keep a newly selected, otherwise-eligible machine's switch disabled for the
	// remainder of a previous machine's in-flight request.
	const [submittingForMachineId, setSubmittingForMachineId] = useState(null);

	// A stale optimistic value must not leak across machine selections or survive a parent
	// refresh: whenever the selected machine changes, or the parent's own source of truth
	// changes (after a successful mutation), the displayed toggle re-syncs to it.
	// partnerPreviewEnrollmentsRefreshCount is included on its own: a completed refresh can
	// report the same enabled value the optimistic update already showed, in which case
	// partnerPreviewEnrollmentEnabled would not change, but the displayed value must still be
	// re-derived from the refreshed prop rather than left standing on the optimistic guess.
	useEffect(() => {
		setDisplayedPreviewUpdatesEnabled(Boolean(partnerPreviewEnrollmentEnabled));
	}, [machine.id, partnerPreviewEnrollmentEnabled, partnerPreviewEnrollmentsRefreshCount]);

	// Tracks which machine is currently selected so an in-flight toggle can tell, once it
	// settles, whether the user has since moved on to a different machine.
	const selectedMachineIdRef = useRef(machine.id);

	useEffect(() => {
		selectedMachineIdRef.current = machine.id;
	}, [machine.id]);

	useEffect(() => {
		loadMachineDetailsData();
	}, [machine]);

	const loadMachineDetailsData = async () => {
		try {
			setSerenityEventsSettings(await settingsService.getSerenityEventsSettings());
			setLoading(false);
		} catch (error) {
			pushDangerNotification('Failed to fetch machine details data.');
		}
	};

	const sendSerenityRequestEmail = async () => {
		try {
			await settingsService.sendSerenityRequestEmail(loggedInUser.id, machine.serialNumber);
			pushSuccessfulNotification('SerenITy request has been successfully sent.');
		} catch (error) {
			pushDangerNotification('Failed to send SerenITy request.');
		}
	};

	const isEligibleForPreviewUpdates = isOSVersionAtLeast(machine.osVersion, MINIMUM_PREVIEW_UPDATES_OS_VERSION);
	const canTogglePreviewUpdates = canManagePartnerPreview && isEligibleForPreviewUpdates;

	// Optimistic with revert: the switch flips immediately, the parent mutation runs, and only a
	// rejection rolls the displayed value back to the last value the parent actually persisted
	// (the `partnerPreviewEnrollmentEnabled` prop). The parent already pushes both notifications.
	// The toggled machine id and the revert value are both captured at click time, since by the
	// time the request settles the user may have selected a different machine: the resync effect
	// above already owns that machine's displayed value, so the revert must not overwrite it.
	const handleTogglePreviewUpdates = async () => {
		const nextPreviewUpdatesEnabled = !displayedPreviewUpdatesEnabled;
		const toggledMachineId = machine.id;
		const revertTo = Boolean(partnerPreviewEnrollmentEnabled);

		setDisplayedPreviewUpdatesEnabled(nextPreviewUpdatesEnabled);
		setSubmittingForMachineId(toggledMachineId);

		try {
			await onTogglePreviewUpdates(machine, nextPreviewUpdatesEnabled);
		} catch (error) {
			if (selectedMachineIdRef.current === toggledMachineId) {
				setDisplayedPreviewUpdatesEnabled(revertTo);
			}
		} finally {
			// A newer toggle (on this machine or another) may already be in flight by the time
			// this one settles; only clear the indicator if it still belongs to this request.
			setSubmittingForMachineId((currentSubmittingMachineId) => {
				return currentSubmittingMachineId === toggledMachineId ? null : currentSubmittingMachineId;
			});
		}
	};

	const maintenanceStatus = MaintenanceStatusFormatter.maintenanceStatus(machine.maintenanceServices[0].expiresOn);

	const currentlyActiveMaintenancePlan = CurrentlyActiveMaintenancePlan(machine.maintenanceServices[0]);

	// An unknown interval (the page's setting call has not resolved) makes the badge Expired or Active only, never
	// Expiring, so the pane agrees with the list, which shows no icon until the interval is known.
	const maintenanceExpiry = getMaintenanceExpiry(machine.maintenanceServices[0].expiresOn, expiringInterval);
	const maintenanceValidityStatus = maintenanceExpiry?.status ?? VALIDITY_STATUS.ACTIVE;
	const isSoftwareOnlyTrial = machine.maintenanceServices[0].maintenancePlan.customerFacingName.toLowerCase() === 'software only trial';

	// The page decides what the section shows (buildAddonsPanel); the pane only renders a ready or a failed panel.
	const showAddonsSection = addons.status === ADDONS_PANEL_STATUS.READY || addons.status === ADDONS_PANEL_STATUS.FAILED;
	
	const durationAsDays = (duration, durationType) => {
		if (durationType === 'days') {
			return duration;
		} else if (durationType === 'months') {
			return duration * 30;
		} else {
			return duration * 365;
		}
	};

	const humanizeDuration = (duration, durationType) => {
		const momentDuration = moment.duration(duration, durationType);
		const years = momentDuration.years();
		const months = momentDuration.months();
		const days = momentDuration.days();

		return `${years > 0 ? `${years} ${years === 1 ? 'Year' : 'Years'}` : ''}${months > 0 ? `${years > 0 ? ', ' : ''}${months} ${months === 1 ? 'Month' : 'Months'}` : ''}${days > 0 ? `${years > 0 || months > 0 ? ', ' : ''}${days} ${days === 1 ? 'Day' : 'Days'}` : ''}`;
	};

	const getValidityDuration = ({ duration, durationType, maintenanceServiceRenewals }) => {
		let durationAndRenewals = durationAsDays(duration, durationType);

		if (maintenanceServiceRenewals && maintenanceServiceRenewals.length > 0) {
			maintenanceServiceRenewals.forEach((maintenanceExtension) => {
				durationAndRenewals += durationAsDays(maintenanceExtension.duration, maintenanceExtension.durationType);
			});
		}
		
		return humanizeDuration(durationAndRenewals, 'days');
	};

	const activationDate = DateFormatter.formatDateToSpecificFormat(machine, 'activationDate', 'DD/MM/YYYY');

	const expirationDate = DateFormatter.formatDateToSpecificFormat(
		machine.maintenanceServices[0],
		'expiresOn',
		'DD/MM/YYYY'
	);

	const manualDownloadUrl = `https://storage.googleapis.com/central-manuals/user-guide-syneto-os-${parseOSVersion(machine.osVersion)}.pdf`;

	function renderSerenityEnabled(serenityEnabled) {
		let hasSerenity, status, message;
		hasSerenity = currentlyActiveMaintenancePlan.hasSerenity;

		status = 'Not supported';
		message = (
			<div className="m-1">
				<div className="pb-3">
					SerenITy is the pro-active support service created by Syneto for constant monitoring of systems and sending
					automatic alerts to Customer Support.
				</div>
				<div className="pb-3">
					This machine is not entitled for the SerenITy service. Our sales team can get in touch to inform you how it
					can be activated.
				</div>
				<div>
					If you want to learn how, click the [Send request] button below. You will be contacted at the following email
					address:
				</div>
				<div className="pb-3 font-weight-500">{loggedInUser.email}</div>
				<div className="container p-0">
					<div className="float-left pt-2 mb-3">
						<a
							className="btn-link"
							href={serenityEventsSettings.serenityMarketingSite}
							target="_blank"
							rel="noopener noreferrer"
						>
							Learn more <Icon name="fa fa-external-link" />
						</a>
					</div>
					<div className="float-right mb-3">
						<Button type="submit" onClick={sendSerenityRequestEmail}>
							Send request
						</Button>
					</div>
				</div>
			</div>
		);

		if (hasSerenity && serenityEnabled === true) {
			status = 'Active';
			message = (
				<div>
					<div className="pb-2">This machine is protected using a proactive support service called SerenITy.</div>
					<a
						className="btn-link"
						href={serenityEventsSettings.serenityMarketingSite}
						target="_blank"
						rel="noopener noreferrer"
					>
						Learn more <Icon name="fa fa-external-link" />
					</a>
				</div>
			);
		}

		if (hasSerenity && (serenityEnabled === false || serenityEnabled == null)) {
			status = 'Inactive';
			message = (
				<div className="m-1">
					<div className="pb-3">
						This machine is entitled to the proactive support service called SerenITy. However, SerenITy is currently
						disabled for this appliance.
					</div>
					<div className="container p-0">
						<div className="float-left pt-2 pb-3">
							<a
								className="btn-link"
								href={serenityEventsSettings.serenityMarketingSite}
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more <Icon name="fa fa-external-link" />
							</a>
						</div>
					</div>
				</div>
			);
		}

		if (maintenanceStatus === 'Expired') {
			status = 'Inactive';
			message = (
				<div className="m-1">
					<div className="pb-3">
						This machine is no longer protected using a proactive support service called SerenITy, as its maintenance
						has expired.
					</div>
					<div className="container p-0">
						<div className="float-left pt-2 pb-3">
							<a
								className="btn-link"
								href={serenityEventsSettings.serenityMarketingSite}
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more <Icon name="fa fa-external-link" />
							</a>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div style={{ cursor: 'help' }}>
				<Popover body={message} trigger="click">
					<Badge appearance={status === 'Active' ? 'success' : 'danger'}>{status}</Badge>
				</Popover>
			</div>
		);
	}

	return loading ? (
		<Loading />
	) : (
		<>
			<h5 className="mb-4">Machine Details</h5>
			<p className="text-muted">Device Details</p>
			<div className="d-flex justify-content-between">
				<p>Machine Name</p>
				{machine.name ? <p className="text-primary">{machine.name}</p> : 'N/A'}
			</div>
			{partnerPreviewParticipating && (
				<div className="d-flex justify-content-between">
					<p>Version Type</p>
					<UpgradeChannelBadge currentUpgradeChannel={currentUpgradeChannel} />
				</div>
			)}
			<div className="d-flex justify-content-between mb-4">
				<p>Activated by</p>
				{activatedByName === null ? (
					'N/A'
				) : activatedByName.length > 40 ? (
					<Tooltip placement="bottom" theme="light" text={activatedByName}>
						<Badge appearance="secondary">{trimString(activatedByName, 40)}</Badge>
					</Tooltip>
				) : (
					<Badge appearance="secondary">{activatedByName}</Badge>
				)}
			</div>
			<p className="text-muted">Maintenance</p>
			<div className="d-flex justify-content-between">
				<p>Status</p>
				<ValidityBadge status={maintenanceValidityStatus} label={isSoftwareOnlyTrial ? 'Trial' : undefined} />
			</div>
			<div className="d-flex justify-content-between">
				<p>Type</p>
				<div>
					{currentlyActiveMaintenancePlan.maintenancePlan.customerFacingName}
					<Popover
						trigger="click"
						body={
							<>
								{currentlyActiveMaintenancePlan.maintenancePlan.endUserDescription}
								<div className="pt-2">
									<a className="btn-link" href="https://syneto.eu/support/" target="_blank" rel="noopener noreferrer">
										Read more <Icon name="fa fa-external-link" />
									</a>
								</div>
							</>
						}
					>
						<span>
							<Icon name="fa fa-question-circle ms-2" />
						</span>
					</Popover>
					<Tooltip placement="left" theme="light" text="Download maintenance certification">
						<a
							className="text-neutral-90"
							style={{ cursor: 'pointer' }}
							onClick={() => {
								return generateMaintenanceCertificationPdf(
									expirationDate,
									currentlyActiveMaintenancePlan.maintenancePlan.customerFacingName,
									machine.serialNumber
								);
							}
							}
						>
							<Icon name="fa fa-file-pdf ms-2" />
						</a>
					</Tooltip>
				</div>
			</div>
			<div className="d-flex justify-content-between">
				<p>Duration</p>
				<p>{getValidityDuration(machine.maintenanceServices[0])}</p>
			</div>
			<div className="d-flex justify-content-between">
				<p>Activation Date</p>
				<p>{activationDate}</p>
			</div>
			<div className="d-flex justify-content-between mb-4">
				<p>Expiration Date</p>
				<p>{expirationDate}</p>
			</div>
			{showAddonsSection && (
				<AddonsSection
					status={addons.status}
					rows={addons.rows}
					request={addonsSectionRequest?.machineId === machine.id ? addonsSectionRequest : null}
					onHandled={onAddonsSectionRequestHandled}
				/>
			)}
			{partnerPreviewParticipating && (
				<>
					<p className="text-muted">Software Update</p>
					{/* align-items-center absorbs the label's bottom margin, which every other row
					    leaves as trailing space -- mb-3 restores it so the next section heading
					    sits at the same distance as the others. */}
					<div className="d-flex justify-content-between align-items-center mb-3">
						<p>Enable SynetoOS Preview Updates</p>
						<div className="d-flex align-items-center">
							{!isEligibleForPreviewUpdates && (
								<>
									<span className="text-muted small me-2">Minimum required version: 6.0.5</span>
									<InfoIcon
										placement="top"
										text="Preview Updates requires SynetoOS 6.0.5 or later on this machine."
										tooltipStyle="mt-0 me-2"
									/>
								</>
							)}
							{canManagePartnerPreview ? (
								<Form.Switch
									checked={displayedPreviewUpdatesEnabled}
									disabled={!canTogglePreviewUpdates || submittingForMachineId === machine.id}
									onChange={() => { return handleTogglePreviewUpdates(); }}
								/>
							) : (
								<Tooltip placement="left" theme="light" text="You need fleet management permission to change this setting.">
									<div className="cannotShowTooltipsOnDisabledButtons">
										<Form.Switch
											checked={displayedPreviewUpdatesEnabled}
											disabled={true}
											onChange={() => {}}
										/>
									</div>
								</Tooltip>
							)}
						</div>
					</div>
				</>
			)}
			<p className="text-muted">Support</p>
			<div className="d-flex justify-content-between">
				<p>SerenITy Status</p>
				{renderSerenityEnabled(machine.serenityEnabled)}
			</div>
			<div className="d-flex justify-content-between">
				<p>Need help?</p>
				<a className="btn-link" href={OPEN_A_TICKET_URL} target="_blank" rel="noopener noreferrer">
					Open a ticket <Icon name="fa fa-external-link" />
				</a>
			</div>
			<div className="d-flex justify-content-between">
				<p>User guide</p>
				<a className="btn-link" href={manualDownloadUrl} target="_blank" rel="noopener noreferrer">
					Download manual <Icon name="fa fa-external-link" />
				</a>
			</div>
			{currentlyActiveMaintenancePlan.hasSerenity &&
				machine.serenityEnabled === true &&
				maintenanceStatus !== 'Expired' && (
				<div className="d-flex justify-content-between">
					<p>SerenITy Events</p>
					<SerenityEventsButton machine={machine} />
				</div>
			)}
		</>
	);
};

MachineDetails.propTypes = {
	machine: PropTypes.object.isRequired,
	partnerPreviewParticipating: PropTypes.bool,
	partnerPreviewEnrollmentEnabled: PropTypes.bool,
	partnerPreviewEnrollmentsRefreshCount: PropTypes.number,
	currentUpgradeChannel: PropTypes.string,
	canManagePartnerPreview: PropTypes.bool,
	onTogglePreviewUpdates: PropTypes.func,
	addons: PropTypes.shape({ status: PropTypes.string, rows: PropTypes.array }),
	expiringInterval: PropTypes.number,
	addonsSectionRequest: PropTypes.shape({ machineId: PropTypes.string }),
	onAddonsSectionRequestHandled: PropTypes.func,
};
